/**
 * ArtistProfilePage — Public-facing artist profile page.
 *
 * Read-only public view with compact identity header:
 *   • Banner with overlapping profile image
 *   • Artist identity row + Follow controls
 *   • Capture carousel
 *   • TipTap rendered published content
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { ArrowLeft, Loader2, Image as ImageIcon, Radio, Sparkles, Check, X, CheckCircle, ChevronDown, BellOff, Bell, UserMinus, Play, Pause, Film, Disc3, Share2 } from 'lucide-react';
import { resolveTenant, checkFollowingArtist, followArtist, unfollowArtist, setNotificationsMuted, checkFanSession, recordPublicEvent, listCaptures, resolveCaptureBySlug, collectCapture } from '../services/api';
import { EVENT_NAMES } from '../config/eventCatalog';
import { getEventCorrelation } from '../utils/eventCorrelation';
import TipTapContentRenderer from '../components/profile/TipTapContentRenderer';
import ReportArtistModal from '../components/ReportArtistModal';
import CaptureShareModal from '../components/capture-sharing/CaptureShareModal';
import { normalizeSharingSuggestions } from '../components/capture-sharing/normalizeSharingSuggestions';
import type { CaptureSharingSuggestion } from '../components/capture-sharing/types';

import 'swiper/css';
import 'swiper/css/effect-coverflow';
import '../components/fanzone/CrateDiggingCarousel.css';
import './ArtistProfilePage.css';

// ─── Types ──────────────────────────────────────────────────────────────────

interface BannerSrcset {
  [width: string]: string;
}

interface PromotedItem {
  type: 'vault' | 'capture';
  id: number;
  title: string;
  coverUrl: string | null;
}

interface CaptureItem {
  id: number;
  slug: string;
  title?: string;
  imageUrl?: string | null;
}

interface TenantData {
  id: number;
  slug: string;
  artistName: string;
  artistNumber?: number | null;
  primaryGenreId?: number | null;
  additionalGenreIds?: number[];
  selectedGenres?: Array<{ id: number; groupId: number; name: string; slug: string; isPrimary: boolean }>;
  bannerUrl: string | null;
  bannerSrcset: BannerSrcset | null;
  profileUrl: string | null;
  statusText: string | null;
  publishedContent: string | null;
  promotedContent: PromotedItem | null;
}

interface ArtistProfilePageProps {
  artistSlug: string;
}

function getArtistEventCorrelation() {
  return getEventCorrelation(window.location.search, false);
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildSrcSetString(srcset: BannerSrcset, originalUrl: string, originalWidth: number): string {
  const entries: string[] = [];
  const widths = Object.keys(srcset).map(Number).sort((a, b) => a - b);
  for (const w of widths) {
    const url = srcset[String(w)];
    if (url) entries.push(`${url} ${w}w`);
  }
  entries.push(`${originalUrl} ${originalWidth}w`);
  return entries.join(', ');
}

const DEFAULT_PROFILE_IMAGE = '/fans-audio-default-profile.png';

type FollowModalState = 'hidden' | 'email' | 'loading' | 'error' | 'success';
type FollowModalIntent = 'follow' | 'collect';

function parseNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeCaptureId(value: unknown): number | null {
  const n = parseNullableNumber(value);
  return n == null ? null : Math.trunc(n);
}

const METER_BAR_COUNT = 8;

function createIdleMeterLevels(count = METER_BAR_COUNT): number[] {
  return Array.from({ length: count }, (_, i) => 0.18 + (i % 3) * 0.04);
}

function computeMeterLevels(data: Uint8Array, bars = METER_BAR_COUNT): number[] {
  if (!data.length || bars <= 0) return createIdleMeterLevels(bars);

  const bucketSize = Math.max(1, Math.floor(data.length / bars));
  const levels: number[] = [];

  for (let i = 0; i < bars; i++) {
    const start = i * bucketSize;
    const end = Math.min(data.length, start + bucketSize);
    let sum = 0;
    let count = 0;
    for (let j = start; j < end; j++) {
      sum += data[j] ?? 0;
      count += 1;
    }
    const avg = count > 0 ? sum / count : 0;
    const normalized = Math.max(0, Math.min(1, avg / 255));
    // Slight curve for readability on quieter material.
    levels.push(0.12 + Math.pow(normalized, 0.7) * 0.88);
  }

  return levels;
}

function computeAnimatedFallbackLevels(timeMs: number, bars = METER_BAR_COUNT): number[] {
  const t = timeMs / 1000;
  return Array.from({ length: bars }, (_, i) => {
    const a = Math.sin(t * (2.2 + i * 0.11) + i * 0.7) * 0.5 + 0.5;
    const b = Math.sin(t * (3.4 + i * 0.09) + i * 1.3) * 0.5 + 0.5;
    const combined = 0.55 * a + 0.45 * b;
    return 0.18 + combined * 0.74;
  });
}

function isMediaSourceSafeForAnalyser(mediaEl: HTMLMediaElement): boolean {
  const raw = mediaEl.currentSrc || mediaEl.src;
  if (!raw) return false;
  try {
    const url = new URL(raw, window.location.href);
    if (url.protocol === 'blob:' || url.protocol === 'data:') return true;
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
}

// ─── Confetti colors ────────────────────────────────────────────────────────
const CONFETTI_COLORS = ['#00F5FF', '#4ade80', '#facc15', '#f472b6', '#a78bfa', '#fb923c'];

interface CaptureCarouselItem {
  id: number;
  slug: string;
  vaultId: number | null;
  coverUrl: string | null;
  mediaType: 'track' | 'video' | 'stream' | 'youtube';
  title: string;
  description: string | null;
  imageUrl: string | null;
  videoThumbnailUrl: string | null;
  videoUrl: string | null;
  youtubeUrl: string | null;
  youtubeAspectRatio: '16:9' | '9:16';
  backgroundMode: 'automatic' | 'solid' | 'image';
  backgroundColor: string;
  backgroundImageUrl: string | null;
  backgroundOverlayOpacity: number;
  audioStart: number;
  playbackLength: number | null;
  playbackType: 'preview' | 'full';
  streamUrl: string | null;
  songLength: number | null;
  streamLinks: Array<{ service: string; url: string }>;
  sharingSuggestions: CaptureSharingSuggestion[];
  collectable: boolean;
  collected: boolean;
}

function getSpotifyEmbedUrl(streamLinks: Array<{ service: string; url: string }>): string | null {
  if (!Array.isArray(streamLinks) || streamLinks.length === 0) return null;

  const spotifyLink = streamLinks.find((row) => {
    const service = String(row.service || '').toLowerCase();
    const url = String(row.url || '').toLowerCase();
    return service.includes('spotify') || url.includes('open.spotify.com/track/');
  });

  if (!spotifyLink?.url) return null;
  const match = spotifyLink.url.match(/open\.spotify\.com\/track\/([A-Za-z0-9]+)/i);
  if (!match?.[1]) return null;
  return `https://open.spotify.com/embed/track/${match[1]}?utm_source=generator`;
}

function buildYoutubeEmbedUrl(baseUrl: string, options?: { autoplay?: boolean }): string {
  try {
    const url = new URL(baseUrl);
    url.searchParams.set('rel', '0');
    url.searchParams.set('modestbranding', '1');
    url.searchParams.set('playsinline', '1');
    url.searchParams.set('iv_load_policy', '3');
    if (options?.autoplay) {
      url.searchParams.set('autoplay', '1');
    }
    return url.toString();
  } catch {
    return baseUrl;
  }
}

function MiniLevelMeter({ active, levels }: { active: boolean; levels: number[] }) {
  return (
    <div className="flex items-end gap-[2px] h-5 px-1 pointer-events-none" aria-hidden="true">
      {levels.map((level, i) => (
        <span
          key={i}
          className="w-[2px] rounded-full bg-white/80 origin-bottom transition-[height,opacity] duration-100"
          style={{
            height: `${Math.max(14, Math.min(100, level * 100))}%`,
            opacity: active ? 0.95 : 0.35,
          }}
        />
      ))}
    </div>
  );
}

function CaptureCarouselCard({
  item,
  isActive,
  onRequestPauseOthers,
  collectBusy,
  onCollect,
  onShare,
}: {
  item: CaptureCarouselItem;
  isActive: boolean;
  onRequestPauseOthers: (except?: HTMLMediaElement | null) => void;
  collectBusy: boolean;
  onCollect: (captureId: number) => void;
  onShare: (capture: CaptureCarouselItem) => void;
}) {
  const [showVideo, setShowVideo] = useState(false);
  const [showYoutube, setShowYoutube] = useState(false);
  const [isTrackPlaying, setIsTrackPlaying] = useState(false);
  const [trackSegmentElapsed, setTrackSegmentElapsed] = useState(0);
  const [trackMeterLevels, setTrackMeterLevels] = useState<number[]>(() => createIdleMeterLevels());
  const trackAudioRef = useRef<HTMLAudioElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const trackAudioContextRef = useRef<AudioContext | null>(null);
  const trackAnalyserRef = useRef<AnalyserNode | null>(null);
  const trackSourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const trackFreqDataRef = useRef<Uint8Array | null>(null);
  const trackMeterRafRef = useRef<number | null>(null);

  const segmentDuration = (() => {
    if (item.mediaType !== 'track') return null;
    if (item.playbackLength && item.playbackLength > 0) return item.playbackLength;
    if (item.playbackType === 'full' && item.songLength && item.songLength > item.audioStart) {
      return Math.max(0, item.songLength - item.audioStart);
    }
    return null;
  })();
  const spotifyEmbedUrl = item.mediaType === 'stream' ? getSpotifyEmbedUrl(item.streamLinks) : null;
  const youtubePlaybackUrl = item.mediaType === 'youtube' && item.youtubeUrl
    ? buildYoutubeEmbedUrl(item.youtubeUrl, { autoplay: true })
    : null;
  const isYoutubePortrait = item.youtubeAspectRatio === '9:16';

  const stopTrackMeter = useCallback(() => {
    if (trackMeterRafRef.current != null) {
      cancelAnimationFrame(trackMeterRafRef.current);
      trackMeterRafRef.current = null;
    }
    setTrackMeterLevels(createIdleMeterLevels());
  }, []);

  const ensureTrackAnalyser = useCallback(() => {
    if (trackAnalyserRef.current && trackFreqDataRef.current) return true;
    const audio = trackAudioRef.current;
    if (!audio || typeof window === 'undefined') return false;
    if (!isMediaSourceSafeForAnalyser(audio)) return false;

    const Ctx = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return false;

    try {
      const ctx = trackAudioContextRef.current ?? new Ctx();
      trackAudioContextRef.current = ctx;

      if (!trackSourceRef.current) {
        trackSourceRef.current = ctx.createMediaElementSource(audio);
      }

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.82;
      trackSourceRef.current.connect(analyser);
      analyser.connect(ctx.destination);

      trackAnalyserRef.current = analyser;
      trackFreqDataRef.current = new Uint8Array(analyser.frequencyBinCount);
      return true;
    } catch {
      return false;
    }
  }, []);

  const startTrackMeter = useCallback(() => {
    if (trackMeterRafRef.current != null) {
      cancelAnimationFrame(trackMeterRafRef.current);
      trackMeterRafRef.current = null;
    }

    if (!ensureTrackAnalyser()) {
      const fallbackTick = () => {
        setTrackMeterLevels(computeAnimatedFallbackLevels(performance.now()));
        if (!trackAudioRef.current?.paused && isActive) {
          trackMeterRafRef.current = requestAnimationFrame(fallbackTick);
        } else {
          trackMeterRafRef.current = null;
        }
      };
      trackMeterRafRef.current = requestAnimationFrame(fallbackTick);
      return;
    }

    const analyser = trackAnalyserRef.current;
    const freq = trackFreqDataRef.current;
    if (!analyser || !freq) return;

    const tick = () => {
      analyser.getByteFrequencyData(freq);
      setTrackMeterLevels(computeMeterLevels(freq));
      if (!trackAudioRef.current?.paused && isActive) {
        trackMeterRafRef.current = requestAnimationFrame(tick);
      } else {
        trackMeterRafRef.current = null;
      }
    };

    trackMeterRafRef.current = requestAnimationFrame(tick);
  }, [ensureTrackAnalyser, isActive]);

  useEffect(() => {
    if (isActive) return;
    trackAudioRef.current?.pause();
    videoRef.current?.pause();
    setIsTrackPlaying(false);
    setTrackSegmentElapsed(0);
    stopTrackMeter();
    setShowVideo(false);
    setShowYoutube(false);
  }, [isActive, stopTrackMeter]);

  useEffect(() => {
    return () => {
      stopTrackMeter();
      try {
        trackSourceRef.current?.disconnect();
      } catch {
        // Ignore disconnect failures during unmount.
      }
      try {
        trackAnalyserRef.current?.disconnect();
      } catch {
        // Ignore disconnect failures during unmount.
      }
      trackSourceRef.current = null;
      trackAnalyserRef.current = null;
      trackFreqDataRef.current = null;
      if (trackAudioContextRef.current) {
        trackAudioContextRef.current.close().catch(() => {
          // Ignore close failures.
        });
        trackAudioContextRef.current = null;
      }
    };
  }, [stopTrackMeter]);

  const handleTrackTimeUpdate = useCallback(() => {
    const a = trackAudioRef.current;
    if (!a) return;
    const elapsed = Math.max(0, a.currentTime - item.audioStart);
    setTrackSegmentElapsed(elapsed);
    if (segmentDuration == null) return;
    if (elapsed >= segmentDuration) {
      a.pause();
      a.currentTime = Math.max(0, item.audioStart);
      setIsTrackPlaying(false);
      setTrackSegmentElapsed(0);
    }
  }, [item.audioStart, segmentDuration]);

  const handleTrackSeeking = useCallback(() => {
    const a = trackAudioRef.current;
    if (!a) return;
    const minTime = Math.max(0, item.audioStart);
    const maxTime = segmentDuration != null ? minTime + segmentDuration : null;
    if (a.currentTime < minTime) {
      a.currentTime = minTime;
      return;
    }
    if (maxTime != null && a.currentTime > maxTime) {
      a.currentTime = maxTime;
    }
  }, [item.audioStart, segmentDuration]);

  const toggleTrackPlayback = useCallback(async () => {
    const a = trackAudioRef.current;
    if (!a || !isActive) return;

    const start = Math.max(0, item.audioStart);
    const elapsed = Math.max(0, a.currentTime - start);
    const atOrPastEnd = segmentDuration != null && elapsed >= segmentDuration;

    if (a.paused) {
      if (a.currentTime < start || atOrPastEnd) {
        a.currentTime = start;
        setTrackSegmentElapsed(0);
      }
      try {
        await a.play();
      } catch {
        setIsTrackPlaying(false);
      }
      return;
    }

    a.pause();
  }, [isActive, item.audioStart, segmentDuration]);

  const seekTrackSegment = useCallback((seconds: number) => {
    const a = trackAudioRef.current;
    if (!a || !isActive) return;
    const maxSeek = segmentDuration != null ? segmentDuration : Math.max(0, seconds);
    const clamped = Math.max(0, Math.min(seconds, maxSeek));
    a.currentTime = Math.max(0, item.audioStart) + clamped;
    setTrackSegmentElapsed(clamped);
  }, [isActive, item.audioStart, segmentDuration]);

  const formatSegmentClock = (seconds: number) => {
    const safe = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(safe / 60).toString();
    const secs = (safe % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const handleStartVideo = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!item.videoUrl) return;
    const v = videoRef.current;
    onRequestPauseOthers(v ?? undefined);
    setShowVideo(true);
    if (v) {
      v.currentTime = 0;
      const promise = v.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {
          // Keep native controls visible for manual play fallback.
        });
      }
    }
  }, [item.videoUrl, onRequestPauseOthers]);

  const handleStartYoutube = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!item.youtubeUrl) return;
    onRequestPauseOthers();
    setShowYoutube(true);
  }, [item.youtubeUrl, onRequestPauseOthers]);

  return (
    <div className="crate-card-perspective">
      <div className="crate-card crate-card-front select-none pointer-events-auto">
        <div className="relative w-full h-full bg-[#0D0D0D]">
          {item.mediaType === 'video' ? (
            <>
              {item.videoUrl ? (
                <>
                <video
                  ref={videoRef}
                  src={item.videoUrl}
                      crossOrigin="anonymous"
                  controls={isActive}
                  playsInline
                  preload="metadata"
                  onClick={(e) => e.stopPropagation()}
                  className={`w-full h-full object-contain bg-black transition-opacity duration-200 ${showVideo ? 'opacity-100' : 'opacity-0'} ${isActive && showVideo ? 'pointer-events-auto' : 'pointer-events-none'}`}
                />

                  {!showVideo && (
                    <>
                      {item.videoThumbnailUrl || item.imageUrl || item.coverUrl ? (
                        <img
                          src={item.videoThumbnailUrl || item.imageUrl || item.coverUrl || ''}
                          alt={`${item.title} thumbnail`}
                          draggable={false}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 w-full h-full bg-[#0D0D0D] flex items-center justify-center">
                          <Film className="w-10 h-10 text-white/70" />
                        </div>
                      )}

                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="w-14 h-14 rounded-full bg-white/90 text-black flex items-center justify-center shadow-lg">
                          <Play className="w-7 h-7 ml-0.5" />
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleStartVideo}
                        disabled={!isActive}
                        data-media-control="true"
                        className="absolute inset-0 bg-transparent"
                        aria-label="Play video capture"
                      />
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <p className="text-xs font-mono text-white/70">Video source unavailable</p>
                </div>
              )}
            </>
          ) : item.mediaType === 'youtube' ? (
            <>
              {showYoutube && youtubePlaybackUrl ? (
                <div className={`absolute inset-0 w-full h-full bg-black transition-opacity duration-200 ${isActive ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                  <div className="absolute inset-0 flex items-center justify-center p-3">
                    <div
                      className={`relative w-full overflow-hidden rounded-lg bg-black ${isYoutubePortrait ? 'max-w-[260px] h-full max-h-[520px]' : ''}`}
                      style={isYoutubePortrait ? undefined : { aspectRatio: '16 / 9' }}
                    >
                      <iframe
                        title={item.title ? `${item.title} on YouTube` : 'YouTube player'}
                        src={youtubePlaybackUrl}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full"
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              {!showYoutube && (
                <>
                  {item.videoThumbnailUrl || item.imageUrl || item.coverUrl ? (
                    <img
                      src={item.videoThumbnailUrl || item.imageUrl || item.coverUrl || ''}
                      alt={`${item.title} thumbnail`}
                      draggable={false}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 w-full h-full bg-[#0D0D0D] flex items-center justify-center">
                      <Film className="w-10 h-10 text-white/70" />
                    </div>
                  )}

                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="w-14 h-14 rounded-full bg-white/90 text-black flex items-center justify-center shadow-lg">
                      <Play className="w-7 h-7 ml-0.5" />
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleStartYoutube}
                    disabled={!isActive}
                    data-media-control="true"
                    className="absolute inset-0 bg-transparent"
                    aria-label="Play YouTube capture"
                  />
                </>
              )}
            </>
          ) : item.mediaType === 'stream' ? (
            <>
              {item.imageUrl || item.coverUrl ? (
                <img
                  src={item.imageUrl || item.coverUrl || ''}
                  alt={item.title}
                  draggable={false}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-[#0D0D0D] flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-text-muted" />
                </div>
              )}

              <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/85 via-black/60 to-transparent">
                <div className="rounded-lg border border-white/10 bg-black/55 backdrop-blur-sm px-3 py-2.5">
                  {spotifyEmbedUrl ? (
                    <iframe
                      title={item.title ? `${item.title} on Spotify` : 'Spotify Player'}
                      src={spotifyEmbedUrl}
                      width="100%"
                      height="80"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      className="w-full rounded-lg"
                    />
                  ) : (
                    <div className="w-full rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-300 text-xs px-3 py-2 flex items-center justify-center text-center">
                      Spotify embed unavailable for this stream capture.
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {item.imageUrl || item.coverUrl ? (
                <img
                  src={item.imageUrl || item.coverUrl || ''}
                  alt={item.title}
                  draggable={false}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-[#0D0D0D] flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-text-muted" />
                </div>
              )}

              <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/85 via-black/60 to-transparent">
                <div className="rounded-lg border border-white/10 bg-black/55 backdrop-blur-sm px-3 py-2.5">
                  <p className="text-xs text-white font-semibold truncate">{item.title}</p>
                  <p className="text-[11px] text-white/70 truncate mb-2">{item.description || 'No description set for this capture yet.'}</p>
                  {item.streamUrl ? (
                    <>
                      <audio
                        ref={trackAudioRef}
                        src={item.streamUrl}
                          crossOrigin="anonymous"
                        data-media-control="true"
                        preload="metadata"
                        onPlay={() => {
                          const a = trackAudioRef.current;
                          if (a && a.currentTime < Math.max(0, item.audioStart)) {
                            a.currentTime = Math.max(0, item.audioStart);
                          }
                          if (trackAudioContextRef.current?.state === 'suspended') {
                            trackAudioContextRef.current.resume().catch(() => {
                              // Resume may fail if browser blocks audio context.
                            });
                          }
                          setIsTrackPlaying(true);
                          onRequestPauseOthers(a);
                          startTrackMeter();
                        }}
                        onPause={() => {
                          setIsTrackPlaying(false);
                          stopTrackMeter();
                        }}
                        onEnded={() => {
                          setIsTrackPlaying(false);
                          stopTrackMeter();
                        }}
                        onSeeking={handleTrackSeeking}
                        onLoadedMetadata={() => {
                          const a = trackAudioRef.current;
                          if (!a) return;
                          a.currentTime = Math.max(0, item.audioStart);
                          setTrackSegmentElapsed(0);
                        }}
                        onTimeUpdate={handleTrackTimeUpdate}
                        className="hidden"
                      />

                      <div className={`flex items-center gap-2 ${isActive ? '' : 'opacity-70'}`}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void toggleTrackPlayback();
                          }}
                          disabled={!isActive}
                          className="w-8 h-8 shrink-0 rounded-full bg-white/90 text-black flex items-center justify-center hover:bg-white transition-colors disabled:opacity-55 disabled:cursor-not-allowed"
                          aria-label={isTrackPlaying ? 'Pause capture preview' : 'Play capture preview'}
                        >
                          {isTrackPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                        </button>

                        <MiniLevelMeter active={isTrackPlaying && isActive} levels={trackMeterLevels} />

                        <input
                          type="range"
                          min={0}
                          max={segmentDuration != null ? segmentDuration : Math.max(trackSegmentElapsed, 1)}
                          step={0.1}
                          value={segmentDuration != null ? Math.min(trackSegmentElapsed, segmentDuration) : trackSegmentElapsed}
                          onChange={(e) => {
                            e.stopPropagation();
                            seekTrackSegment(Number(e.target.value));
                          }}
                          disabled={!isActive}
                          className="flex-1 h-1.5 accent-white cursor-pointer disabled:cursor-not-allowed"
                          aria-label="Capture preview progress"
                        />

                        <span className="text-[11px] text-white/75 font-mono shrink-0 tabular-nums">
                          {formatSegmentClock(trackSegmentElapsed)}
                          {segmentDuration != null ? ` / ${formatSegmentClock(segmentDuration)}` : ''}
                        </span>
                      </div>
                    </>
                  ) : (
                    <p className="text-xs font-mono text-white/70 text-center">Track source unavailable</p>
                  )}
                </div>
              </div>
            </>
          )}

          {(item.collectable || item.collected) && (
            <div className="absolute right-3 top-3 z-20">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!item.collected && !collectBusy) {
                    onCollect(item.id);
                  }
                }}
                disabled={item.collected || collectBusy || !isActive}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-mono tracking-wide backdrop-blur-sm transition ${
                  item.collected
                    ? 'border-white/20 bg-white/10 text-white/70 cursor-not-allowed'
                    : 'border-white/25 bg-black/45 text-white hover:bg-black/65'
                } ${!isActive ? 'opacity-70' : ''}`}
                aria-label={item.collected ? 'Collected' : 'Collect'}
              >
                {collectBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Disc3 className="w-3 h-3" />}
                <span>{item.collected ? 'Collected' : 'Collect'}</span>
              </button>
            </div>
          )}

          <div className="absolute left-3 top-3 z-20">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onShare(item);
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-black/45 px-2.5 py-1 text-[11px] font-mono tracking-wide text-white hover:bg-black/65 transition"
              aria-label="Share this capture"
            >
              <Share2 className="w-3 h-3" />
              <span>Share</span>
            </button>
          </div>

          <div className="absolute inset-x-0 bottom-0 p-3 pointer-events-none">
            <p className="text-xs font-mono text-white/85 text-center">
              {isActive ? (item.mediaType === 'video' && !showVideo ? 'tap play to start video' : '') : 'tap side to center'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function ArtistProfilePage({ artistSlug }: ArtistProfilePageProps) {
  const [tenantData, setTenantData] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [notificationsMuted, setNotificationsMutedState] = useState(false);

  // Follow flow state
  const [followLoading, setFollowLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [modalState, setModalState] = useState<FollowModalState>('hidden');
  const [modalIntent, setModalIntent] = useState<FollowModalIntent>('follow');
  const [pendingCollectCaptureId, setPendingCollectCaptureId] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [modalError, setModalError] = useState('');
  const [hasFanSession, setHasFanSession] = useState(false);
  const [fanUserReference, setFanUserReference] = useState<string | null>(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [collectingCaptureIds, setCollectingCaptureIds] = useState<Record<number, boolean>>({});
  const followBtnRef = useRef<HTMLButtonElement>(null);

  // Report modal
  const [showReport, setShowReport] = useState(false);

  // Following dropdown & unfollow confirmation
  const [showFollowingDropdown, setShowFollowingDropdown] = useState(false);
  const [showUnfollowConfirm, setShowUnfollowConfirm] = useState(false);
  const [unfollowLoading, setUnfollowLoading] = useState(false);
  const [muteLoading, setMuteLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasRecordedProfileViewRef = useRef(false);
  const [captureCarouselItems, setCaptureCarouselItems] = useState<CaptureCarouselItem[]>([]);
  const [shareCapture, setShareCapture] = useState<CaptureCarouselItem | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [capturesLoading, setCapturesLoading] = useState(false);
  const [activeCaptureIndex, setActiveCaptureIndex] = useState(0);
  const captureSwiperRef = useRef<SwiperType | null>(null);
  const captureCarouselWrapRef = useRef<HTMLDivElement | null>(null);
  const activeCaptureIndexRef = useRef(0);
  const pendingCenterIndexRef = useRef(0);

  const recenterCaptureCarousel = useCallback((index?: number) => {
    const apply = () => {
      const swiper = captureSwiperRef.current;
      if (!swiper) return;
      const target = typeof index === 'number' ? index : activeCaptureIndexRef.current;
      swiper.update();
      swiper.slideTo(target, 0, false);
    };

    requestAnimationFrame(() => {
      apply();
      // Some late style/image/layout updates land one frame later.
      requestAnimationFrame(apply);
    });

    // Late CSS/layout application can still skew initial wrapper translate.
    window.setTimeout(apply, 120);
    window.setTimeout(apply, 360);
  }, []);

  const pauseAllCaptureMedia = useCallback((except?: HTMLMediaElement | null) => {
    const root = captureCarouselWrapRef.current;
    if (!root) return;

    const media = root.querySelectorAll('audio, video');
    media.forEach((node) => {
      if (except && node === except) return;
      try {
        (node as HTMLMediaElement).pause();
      } catch {
        // Ignore pause failures from detached/unready nodes.
      }
    });
  }, []);

  const handleCapturePrev = useCallback(() => {
    if (!captureCarouselItems.length) return;
    pauseAllCaptureMedia();
    captureSwiperRef.current?.slidePrev();
  }, [captureCarouselItems.length, pauseAllCaptureMedia]);

  const handleCaptureNext = useCallback(() => {
    if (!captureCarouselItems.length) return;
    pauseAllCaptureMedia();
    captureSwiperRef.current?.slideNext();
  }, [captureCarouselItems.length, pauseAllCaptureMedia]);

  useEffect(() => {
    if (!artistSlug) return;

    async function load() {
      try {
        const res = await resolveTenant(artistSlug);
        if (!res.ok) {
          setNotFound(true);
          return;
        }
        const json = (await res.json()) as { data: TenantData };
        setTenantData(json.data);

        // Record one profile-view event for this page load.
        if (!hasRecordedProfileViewRef.current) {
          hasRecordedProfileViewRef.current = true;
          recordPublicEvent({
            event: EVENT_NAMES.ARTIST_PROFILE_VIEW,
            artistSlug,
            ...getArtistEventCorrelation(),
          }).catch((err) => {
            console.warn('[Events] Failed to record artist.profile.view:', err);
          });
        }

        // Check fan session
        try {
          const sessionRes = await checkFanSession();
          if (sessionRes.ok) {
            const sessionData = (await sessionRes.json()) as {
              authenticated: boolean;
              user?: { userReference?: string | null };
            };
            setHasFanSession(sessionData.authenticated);
            setFanUserReference(sessionData.user?.userReference || null);
          }
        } catch {
          // Silently fail
        }

        // Check if the current fan is following this artist
        try {
          const followRes = await checkFollowingArtist(artistSlug);
          if (followRes.ok) {
            const followData = (await followRes.json()) as { following: boolean; notificationsMuted?: boolean };
            setIsFollowing(followData.following);
            if (followData.notificationsMuted !== undefined) {
              setNotificationsMutedState(followData.notificationsMuted);
            }
          }
        } catch {
          // Silently fail — fan session might not exist
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [artistSlug]);

  useEffect(() => {
    let cancelled = false;

    async function loadCaptureCarousel() {
      setCapturesLoading(true);
      try {
        const capturesRes = await listCaptures(artistSlug);
        if (!capturesRes.ok) {
          if (!cancelled) setCaptureCarouselItems([]);
          return;
        }

        const capturesJson = await capturesRes.json() as { data: CaptureItem[] };
        const captures = capturesJson.data || [];
        const configuredPromotedCaptureId =
          tenantData?.promotedContent?.type === 'capture'
            ? normalizeCaptureId(tenantData.promotedContent.id)
            : null;

        const resolved = await Promise.all(
          captures.map(async (capture) => {
            try {
              const captureRes = await resolveCaptureBySlug(artistSlug, capture.slug);
              if (!captureRes.ok) return null;

              const captureJson = await captureRes.json() as {
                data: {
                  id: number;
                  title: string;
                  description?: string | null;
                  vaultId?: number | null;
                  collectable?: boolean;
                  collected?: boolean;
                  mediaType?: 'track' | 'video' | 'stream' | 'youtube';
                  imageUrl?: string | null;
                  videoThumbnailUrl?: string | null;
                  videoUrl?: string | null;
                  youtubeUrl?: string | null;
                  youtubeAspectRatio?: '16:9' | '9:16';
                  backgroundMode?: 'automatic' | 'solid' | 'image';
                  backgroundColor?: string | null;
                  backgroundImageUrl?: string | null;
                  backgroundOverlayOpacity?: number;
                  audioStart?: number | null;
                  playbackLength?: number | null;
                  playbackType?: 'preview' | 'full';
                  sharingSuggestions?: unknown;
                  track?: {
                    streamUrl?: string | null;
                    songLength?: number | null;
                    streamLinks?: Array<{ service: string; url: string }>;
                  } | null;
                };
              };

              return {
                id: capture.id,
                slug: capture.slug,
                vaultId: captureJson.data.vaultId ?? null,
                coverUrl: capture.imageUrl || null,
                mediaType:
                  captureJson.data.mediaType === 'video'
                    ? 'video'
                    : (captureJson.data.mediaType === 'stream' ? 'stream' : (captureJson.data.mediaType === 'youtube' ? 'youtube' : 'track')),
                title: captureJson.data.title,
                description: captureJson.data.description || null,
                imageUrl: captureJson.data.imageUrl || null,
                videoThumbnailUrl: captureJson.data.videoThumbnailUrl || null,
                videoUrl: captureJson.data.videoUrl || null,
                youtubeUrl: captureJson.data.youtubeUrl || null,
                youtubeAspectRatio: captureJson.data.youtubeAspectRatio === '9:16' ? '9:16' : '16:9',
                backgroundMode: captureJson.data.backgroundMode === 'solid' || captureJson.data.backgroundMode === 'image' ? captureJson.data.backgroundMode : 'automatic',
                backgroundColor: captureJson.data.backgroundColor || '#0D0D0D',
                backgroundImageUrl: captureJson.data.backgroundImageUrl || null,
                backgroundOverlayOpacity: Math.max(0, Math.min(90, Number(captureJson.data.backgroundOverlayOpacity ?? 35))),
                audioStart: Math.max(0, parseNullableNumber(captureJson.data.audioStart) ?? 0),
                playbackLength: (() => {
                  const n = parseNullableNumber(captureJson.data.playbackLength);
                  return n != null && n > 0 ? n : null;
                })(),
                playbackType: captureJson.data.playbackType === 'full' ? 'full' : 'preview',
                streamUrl: captureJson.data.track?.streamUrl || null,
                songLength: (() => {
                  const n = parseNullableNumber(captureJson.data.track?.songLength);
                  return n != null && n > 0 ? n : null;
                })(),
                streamLinks: Array.isArray(captureJson.data.track?.streamLinks)
                  ? captureJson.data.track!.streamLinks!.filter((row) => row && row.url)
                  : [],
                sharingSuggestions: normalizeSharingSuggestions(captureJson.data.sharingSuggestions),
                collectable: captureJson.data.collectable === true,
                collected: captureJson.data.collected === true,
              } as CaptureCarouselItem;
            } catch {
              return null;
            }
          })
        );

        if (!cancelled) {
          const nextItems = resolved.filter((item): item is CaptureCarouselItem => Boolean(item));
          let arrangedItems = nextItems;

          // Keep promoted capture centerable with visible neighbors in 3+ sets.
          let promotedIndex =
            configuredPromotedCaptureId == null
              ? -1
              : nextItems.findIndex((item) => normalizeCaptureId(item.id) === configuredPromotedCaptureId);

          if (arrangedItems.length >= 3 && promotedIndex >= 0) {
            arrangedItems = [...arrangedItems];
            if (promotedIndex === 0) {
              const first = arrangedItems[0];
              arrangedItems[0] = arrangedItems[1];
              arrangedItems[1] = first;
              promotedIndex = 1;
            } else if (promotedIndex === arrangedItems.length - 1) {
              const tail = arrangedItems.length - 1;
              const last = arrangedItems[tail];
              arrangedItems[tail] = arrangedItems[tail - 1];
              arrangedItems[tail - 1] = last;
              promotedIndex = tail - 1;
            }
          }

          const initialIndex =
            promotedIndex >= 0
              ? promotedIndex
              : (arrangedItems.length >= 3 ? 1 : 0);

          setCaptureCarouselItems(arrangedItems);

          pendingCenterIndexRef.current = initialIndex;
          activeCaptureIndexRef.current = initialIndex;
          setActiveCaptureIndex(initialIndex);

          if (arrangedItems.length > 0) {
            recenterCaptureCarousel(initialIndex);
          }
        }
      } catch {
        if (!cancelled) setCaptureCarouselItems([]);
      } finally {
        if (!cancelled) setCapturesLoading(false);
      }
    }

    if (artistSlug) {
      loadCaptureCarousel();
    }

    return () => {
      cancelled = true;
    };
  }, [artistSlug, recenterCaptureCarousel, tenantData?.promotedContent?.id, tenantData?.promotedContent?.type]);

  useEffect(() => {
    activeCaptureIndexRef.current = activeCaptureIndex;
  }, [activeCaptureIndex]);

  useEffect(() => {
    const swiper = captureSwiperRef.current;
    if (!swiper || captureCarouselItems.length === 0) return;

    recenterCaptureCarousel();
  }, [captureCarouselItems, activeCaptureIndex, recenterCaptureCarousel]);

  useEffect(() => {
    if (!captureCarouselItems.length) return;

    let cancelled = false;
    let timer: number | undefined;
    let attempts = 0;

    const tick = () => {
      if (cancelled) return;
      recenterCaptureCarousel();
      attempts += 1;
      if (attempts < 8) {
        timer = window.setTimeout(tick, 180);
      }
    };

    timer = window.setTimeout(tick, 80);

    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [captureCarouselItems.length, recenterCaptureCarousel]);

  useEffect(() => {
    if (!captureCarouselItems.length) return;

    const handleWindowResize = () => recenterCaptureCarousel();
    const handleWindowLoad = () => recenterCaptureCarousel();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        recenterCaptureCarousel();
      }
    };

    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('load', handleWindowLoad);
    document.addEventListener('visibilitychange', handleVisibility);

    const root = captureCarouselWrapRef.current;
    const ResizeObs = typeof window !== 'undefined' ? window.ResizeObserver : undefined;
    const resizeObserver = ResizeObs && root
      ? new ResizeObs(() => {
          recenterCaptureCarousel();
        })
      : null;

    if (resizeObserver && root) {
      resizeObserver.observe(root);
    }

    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    if (fonts?.ready) {
      fonts.ready.then(() => {
        recenterCaptureCarousel();
      }).catch(() => {
        // Ignore font readiness failures.
      });
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('load', handleWindowLoad);
      document.removeEventListener('visibilitychange', handleVisibility);
      resizeObserver?.disconnect();
    };
  }, [captureCarouselItems.length, recenterCaptureCarousel]);

  const triggerConfetti = useCallback(() => {
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 2000);
  }, []);

  const handleFollowLoggedIn = useCallback(async () => {
    setFollowLoading(true);
    try {
      const res = await followArtist(artistSlug, undefined, getEventCorrelation(window.location.search, true));
      if (res.ok) {
        const data = (await res.json()) as { data: { success: boolean; alreadyFollowing: boolean } };
        setIsFollowing(true);
        triggerConfetti();
      } else {
        const err = (await res.json()) as { error: string };
        console.warn('[Follow] Error:', err.error);
      }
    } catch (err) {
      console.warn('[Follow] Network error:', err);
    } finally {
      setFollowLoading(false);
    }
  }, [artistSlug, triggerConfetti]);

  // Auto-trigger follow if redirected from fan-login with ?follow=1
  useEffect(() => {
    if (loading || isFollowing || !hasFanSession) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('follow') === '1') {
      const url = new URL(window.location.href);
      url.searchParams.delete('follow');
      window.history.replaceState({}, '', url.pathname);
      handleFollowLoggedIn();
    }
  }, [loading, isFollowing, hasFanSession, handleFollowLoggedIn]);

  const handleFollowClick = useCallback(() => {
    if (followLoading) return;

    // If already following, toggle dropdown instead of re-following
    if (isFollowing) {
      setShowFollowingDropdown(prev => !prev);
      return;
    }

    if (hasFanSession) {
      // Logged in — follow directly
      handleFollowLoggedIn();
    } else {
      // Not logged in — show email modal
      setModalIntent('follow');
      setPendingCollectCaptureId(null);
      setModalState('email');
      setEmail('');
      setModalError('');
    }
  }, [isFollowing, followLoading, hasFanSession, handleFollowLoggedIn]);

  const markCaptureCollected = useCallback((captureId: number) => {
    setCaptureCarouselItems((prev) => prev.map((item) => (
      item.id === captureId ? { ...item, collected: true } : item
    )));
  }, []);

  const handleCollectLoggedIn = useCallback(async (captureId: number) => {
    setCollectingCaptureIds((prev) => ({ ...prev, [captureId]: true }));
    try {
      const res = await collectCapture(captureId, {
        ...getEventCorrelation(window.location.search, true),
      });
      if (res.ok) {
        markCaptureCollected(captureId);
        setIsFollowing(true);
        if (!fanUserReference) {
          const data = (await res.json()) as { data?: { userReference?: string } };
          if (data?.data?.userReference) {
            setFanUserReference(data.data.userReference);
          }
        }
      } else {
        const err = (await res.json()) as { error?: string };
        console.warn('[Collect] Error:', err.error || 'collect failed');
      }
    } catch (err) {
      console.warn('[Collect] Network error:', err);
    } finally {
      setCollectingCaptureIds((prev) => {
        const next = { ...prev };
        delete next[captureId];
        return next;
      });
    }
  }, [markCaptureCollected, fanUserReference]);

  const handleCollectClick = useCallback((captureId: number) => {
    if (hasFanSession) {
      void handleCollectLoggedIn(captureId);
      return;
    }

    setModalIntent('collect');
    setPendingCollectCaptureId(captureId);
    setModalState('email');
    setEmail('');
    setModalError('');
  }, [hasFanSession, handleCollectLoggedIn]);

  const handleShareCapture = useCallback((capture: CaptureCarouselItem) => {
    setShareCapture(capture);
    setShareModalOpen(true);
  }, []);

  // Auto-trigger collect if redirected from fan-login with ?collect={captureId}
  useEffect(() => {
    if (loading || !hasFanSession) return;
    const params = new URLSearchParams(window.location.search);
    const collectParam = params.get('collect');
    if (!collectParam) return;

    const captureId = Number(collectParam);
    if (!Number.isFinite(captureId) || captureId <= 0) return;

    const url = new URL(window.location.href);
    url.searchParams.delete('collect');
    window.history.replaceState({}, '', url.pathname);
    void handleCollectLoggedIn(captureId);
  }, [loading, hasFanSession, handleCollectLoggedIn]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showFollowingDropdown) return;
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowFollowingDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFollowingDropdown]);

  // Handle mute/unmute notifications
  const handleToggleMute = useCallback(async () => {
    setMuteLoading(true);
    try {
      const res = await setNotificationsMuted(artistSlug, !notificationsMuted);
      if (res.ok) {
        const data = (await res.json()) as { data: { notificationsMuted: boolean } };
        setNotificationsMutedState(data.data.notificationsMuted);
      }
    } catch (err) {
      console.warn('[Mute] Error:', err);
    } finally {
      setMuteLoading(false);
      setShowFollowingDropdown(false);
    }
  }, [artistSlug, notificationsMuted]);

  // Handle unfollow
  const handleUnfollow = useCallback(async () => {
    setUnfollowLoading(true);
    try {
      const res = await unfollowArtist(artistSlug);
      if (res.ok) {
        setIsFollowing(false);
        setNotificationsMutedState(false);
        setShowUnfollowConfirm(false);
        setShowFollowingDropdown(false);
      }
    } catch (err) {
      console.warn('[Unfollow] Error:', err);
    } finally {
      setUnfollowLoading(false);
    }
  }, [artistSlug]);

  const handleEmailSubmit = useCallback(async () => {
    if (!email.trim()) {
      setModalError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setModalError('Please enter a valid email address');
      return;
    }

    setModalState('loading');
    setModalError('');

    try {
      if (modalIntent === 'collect' && !pendingCollectCaptureId) {
        setModalError('No capture selected for collect. Please try again.');
        setModalState('error');
        return;
      }

      const res = modalIntent === 'collect'
        ? await collectCapture(pendingCollectCaptureId!, { email: email.trim(), ...getEventCorrelation(window.location.search, true) })
        : await followArtist(artistSlug, email.trim(), getEventCorrelation(window.location.search, true));
      if (!res.ok) {
        const errJson = (await res.json()) as { error: string };
        setModalError(errJson.error || 'Something went wrong. Please try again.');
        setModalState('error');
        return;
      }

      if (modalIntent === 'collect') {
        const data = (await res.json()) as { data: { isNewUser?: boolean; userReference?: string } };
        setIsNewUser(Boolean(data.data?.isNewUser));
        if (data.data?.userReference) {
          setFanUserReference(data.data.userReference);
        }
      } else {
        const data = (await res.json()) as { data: { success: boolean; isNewUser: boolean; alreadyFollowing: boolean; userReference?: string; authenticated?: boolean } };
        setIsNewUser(data.data.isNewUser);
        setIsFollowing(true);
        setHasFanSession(Boolean(data.data.authenticated));
        if (data.data.userReference) {
          setFanUserReference(data.data.userReference);
        }
      }
      setModalState('success');
      triggerConfetti();
    } catch {
      setModalError('Network error. Please check your connection and try again.');
      setModalState('error');
    }
  }, [email, artistSlug, triggerConfetti, modalIntent, pendingCollectCaptureId]);

  const handleCloseModal = useCallback(() => {
    setModalState('hidden');
    setModalError('');
    setModalIntent('follow');
    setPendingCollectCaptureId(null);
  }, []);

  // ── Loading state ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
      </div>
    );
  }

  // ── Not found ───────────────────────────────────────────────────────────
  if (notFound || !tenantData) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-3 text-white">Artist Not Found</h1>
          <p className="text-text-secondary font-mono">
            <span className="text-electric-cyan">@{artistSlug}</span> doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  const {
    artistName,
    artistNumber,
    selectedGenres,
    bannerUrl,
    bannerSrcset,
    profileUrl,
    statusText,
    publishedContent,
  } = tenantData;

  const srcSetString =
    bannerSrcset && bannerUrl ? buildSrcSetString(bannerSrcset, bannerUrl, 2400) : undefined;
  const artistNumberLabel = typeof artistNumber === 'number'
    ? (artistNumber < 101 ? `Founding Artist #${artistNumber}` : `Artist #${artistNumber}`)
    : null;

  const displayProfileImage = profileUrl || DEFAULT_PROFILE_IMAGE;

  return (
    <div className="min-h-screen bg-deep-charcoal">
      {/* ── Minimal header ────────────────────────────────────────────────── */}
      {/* ── Banner + overlapping profile image ───────────────────────────── */}
      <div className="relative">
        <div
          className="relative w-full overflow-hidden"
          style={{ aspectRatio: '3 / 1', maxHeight: '360px', minHeight: '170px' }}
        >
          {hasFanSession && fanUserReference && (
            <div className="absolute left-3 top-3 z-30">
              <a
                href={`/my-collection/${fanUserReference}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/45 px-3 py-1.5 text-[11px] sm:text-xs font-mono tracking-wide text-white hover:bg-black/65 transition-colors backdrop-blur-sm"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>View My Collection</span>
              </a>
            </div>
          )}

          {bannerUrl ? (
            <img
              src={bannerUrl}
              srcSet={srcSetString}
              sizes="100vw"
              alt={`${artistName} banner`}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              decoding="async"
            />
          ) : (
            <img
              src="/fans-audio-default-banner.webp"
              alt={`${artistName} banner`}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              decoding="async"
            />
          )}
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/50 via-black/20 to-transparent pointer-events-none" />

          <div className="absolute inset-x-0 bottom-0 pointer-events-none">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4 sm:pb-5 lg:pb-6">
              <div className="ml-[132px] sm:ml-[148px] lg:ml-[164px] min-w-0">
                <motion.h1
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="text-[1.4rem] sm:text-[2rem] lg:text-[2.5em] font-bold text-white tracking-tight leading-tight truncate"
                  style={{ textShadow: '0 2px 16px rgba(0,0,0,0.75)' }}
                >
                  {artistName}
                </motion.h1>

                {artistNumberLabel && (
                  <p
                    className="mt-1 text-[11px] sm:text-xs text-white/85 font-mono tracking-wide"
                    style={{ textShadow: '0 2px 16px rgba(0,0,0,0.75)' }}
                  >
                    {artistNumberLabel}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 z-20 pointer-events-none">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="translate-y-[36%]">
              <div className="w-[120px] h-[120px] sm:w-[136px] sm:h-[136px] lg:w-[152px] lg:h-[152px] rounded-xl overflow-hidden border border-white/25 shadow-[0_10px_28px_rgba(0,0,0,0.5)] bg-black/45">
                <img
                  src={displayProfileImage}
                  alt={`${artistName} profile`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Identity row under banner ────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 sm:pt-4 lg:pt-5">
        <div className="grid grid-cols-[120px_minmax(0,1fr)_auto] sm:grid-cols-[136px_minmax(0,1fr)_auto] lg:grid-cols-[152px_minmax(0,1fr)_auto] items-start gap-4 sm:gap-5 lg:gap-6">
          <div className="w-[120px] sm:w-[136px] lg:w-[152px] shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-0 pt-0">
            <div className="flex flex-wrap items-center gap-1.5">
              {selectedGenres?.map((genre) => (
                <span
                  key={genre.id}
                  className="px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-mono border border-white/12 bg-white/5 text-white/88"
                >
                  {genre.name}
                </span>
              ))}
            </div>

            {statusText && (
              <div className="mt-2 flex items-center gap-2.5 min-w-0">
                <span className="relative flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5">
                  <span className="absolute inset-0 rounded-full bg-green-400/30 animate-ping" />
                  <Radio className="relative text-green-400 w-full h-full" />
                </span>
                <p className="font-mono text-white/85 truncate text-xs sm:text-sm">{statusText}</p>
              </div>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="justify-self-end"
          >
            <div className="relative">
              <AnimatePresence>
                {showConfetti && (
                  <div className="absolute inset-0 pointer-events-none overflow-visible z-10">
                    {Array.from({ length: 30 }).map((_, i) => {
                      const angle = (i / 30) * Math.PI * 2;
                      const distance = 40 + Math.random() * 80;
                      const spreadX = Math.cos(angle) * distance;
                      const spreadY = Math.sin(angle) * distance;
                      return (
                        <motion.div
                          key={i}
                          initial={{
                            opacity: 1,
                            scale: 0,
                            x: 0,
                            y: 0,
                          }}
                          animate={{
                            opacity: [1, 1, 0],
                            x: spreadX,
                            y: spreadY,
                            scale: [0, 1.2, 0.6],
                            rotate: Math.random() * 720 - 360,
                          }}
                          exit={{ opacity: 0 }}
                          transition={{
                            duration: 1.2 + Math.random() * 0.6,
                            delay: Math.random() * 0.2,
                            ease: 'easeOut',
                          }}
                          className="absolute w-2 h-2 rounded-sm"
                          style={{
                            left: '50%',
                            top: '50%',
                            backgroundColor: CONFETTI_COLORS[i % 6],
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </AnimatePresence>

              <button
                ref={followBtnRef}
                onClick={handleFollowClick}
                className={`group relative flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl backdrop-blur-md border font-bold text-sm sm:text-base tracking-wide shadow-lg shadow-black/20 transition-all ${
                  isFollowing
                    ? 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:border-white/20 cursor-pointer'
                    : 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30 active:scale-[0.97] cursor-pointer'
                }`}
              >
                {followLoading ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 text-electric-cyan animate-spin" />
                ) : isFollowing ? (
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-electric-cyan" />
                ) : (
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-electric-cyan transition-transform duration-300 group-hover:rotate-[72deg] group-hover:scale-125" />
                )}
                {followLoading ? 'Following...' : isFollowing ? 'Following' : 'Follow'}
                {isFollowing && (
                  <ChevronDown className={`w-3.5 h-3.5 text-white/50 transition-transform duration-200 ${showFollowingDropdown ? 'rotate-180' : ''}`} />
                )}
              </button>

              <AnimatePresence>
                {showFollowingDropdown && (
                  <motion.div
                    ref={dropdownRef}
                    initial={{ opacity: 0, y: -4, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-white/10 bg-[#1A1A1A]/95 backdrop-blur-xl shadow-2xl overflow-hidden z-50"
                  >
                    <button
                      onClick={handleToggleMute}
                      disabled={muteLoading}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors cursor-pointer disabled:opacity-50"
                    >
                      {muteLoading ? (
                        <Loader2 className="w-4 h-4 text-white/50 animate-spin" />
                      ) : notificationsMuted ? (
                        <Bell className="w-4 h-4 text-electric-cyan" />
                      ) : (
                        <BellOff className="w-4 h-4 text-white/50" />
                      )}
                      {notificationsMuted ? 'Unmute Notifications' : 'Mute Notifications'}
                    </button>

                    <div className="border-t border-white/5" />

                    <button
                      onClick={() => { setShowUnfollowConfirm(true); setShowFollowingDropdown(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors cursor-pointer"
                    >
                      <UserMinus className="w-4 h-4" />
                      Unfollow
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </section>
      {/* ── Main content ─────────────────────────────────────────────────── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* ── All Captures Carousel ───────────────────────────────────────── */}
        {(capturesLoading || captureCarouselItems.length > 0) && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="mt-6 sm:mt-8"
          >
            {capturesLoading && (
              <div className="flex justify-center mb-4">
                <Loader2 className="w-4 h-4 text-electric-cyan animate-spin" />
              </div>
            )}

            {captureCarouselItems.length > 0 && (
              <div
                ref={captureCarouselWrapRef}
                className="crate-carousel-wrapper profile-capture-carousel relative"
              >
                <Swiper
                  effect="slide"
                  grabCursor={false}
                  centeredSlides
                  observer
                  observeParents
                  updateOnWindowResize
                  slidesPerView="auto"
                  slideToClickedSlide={false}
                  speed={350}
                  allowTouchMove={false}
                  simulateTouch={false}
                  preventClicks={false}
                  preventClicksPropagation={false}
                  breakpoints={{
                    0: {
                      slidesPerView: 1.2,
                      spaceBetween: 16,
                    },
                    768: {
                      slidesPerView: 'auto',
                      spaceBetween: 0,
                    },
                  }}
                  onSwiper={(swiper) => {
                    captureSwiperRef.current = swiper;
                    const target = pendingCenterIndexRef.current;
                    swiper.slideTo(target, 0, false);
                    recenterCaptureCarousel(target);
                  }}
                  onSlideChange={(swiper) => {
                    pauseAllCaptureMedia();
                    activeCaptureIndexRef.current = swiper.activeIndex;
                    setActiveCaptureIndex(swiper.activeIndex);
                  }}
                  className="crate-carousel-swiper"
                >
                  {captureCarouselItems.map((capture, index) => (
                    <SwiperSlide
                      key={capture.id}
                      className="crate-slide"
                    >
                      <CaptureCarouselCard
                        item={capture}
                        isActive={index === activeCaptureIndex}
                        onRequestPauseOthers={pauseAllCaptureMedia}
                        collectBusy={collectingCaptureIds[capture.id] === true}
                        onCollect={handleCollectClick}
                        onShare={handleShareCapture}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>

                <button
                  type="button"
                  onClick={handleCapturePrev}
                  className="profile-capture-nav-zone profile-capture-nav-zone-left"
                  aria-label="Previous capture"
                />
                <button
                  type="button"
                  onClick={handleCaptureNext}
                  className="profile-capture-nav-zone profile-capture-nav-zone-right"
                  aria-label="Next capture"
                />

              </div>
            )}
          </motion.section>
        )}

        {/* ── Published TipTap content ───────────────────────────────────── */}
        {publishedContent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-8 sm:mt-10"
          >
            <div className="p-6 sm:p-8 lg:p-10">
              <TipTapContentRenderer content={publishedContent} />
            </div>
          </motion.div>
        )}
      </main>

      {/* ── Report Link ─────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <button
          onClick={() => setShowReport(true)}
          className="text-[11px] text-text-muted/50 hover:text-text-muted transition-colors font-mono"
        >
          Report this artist
        </button>
      </div>

      {/* ── Follow Email Modal ──────────────────────────────────────────── */}
      <AnimatePresence>
        {modalState !== 'hidden' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleCloseModal} />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 60 }}
              transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
              className="relative w-full max-w-md bg-[#1A1A1A]/95 border border-white/10 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl"
            >
              {/* Success State */}
              {modalState === 'success' && (
                <div className="p-8 text-center relative overflow-hidden">
                  {/* Confetti particles */}
                  <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    {Array.from({ length: 40 }).map((_, i) => {
                      const spreadX = (Math.random() - 0.5) * 300;
                      const spreadY = (Math.random() - 0.5) * 300;
                      return (
                        <motion.div
                          key={i}
                          initial={{
                            opacity: 1,
                            scale: 0,
                            x: 0,
                            y: 0,
                          }}
                          animate={{
                            opacity: [1, 1, 0],
                            x: spreadX,
                            y: spreadY,
                            scale: [0, 1.2, 0.6],
                            rotate: Math.random() * 720 - 360,
                          }}
                          transition={{
                            duration: 1.4 + Math.random() * 0.8,
                            delay: Math.random() * 0.3,
                            ease: 'easeOut',
                          }}
                          className="absolute w-2 h-2 rounded-sm"
                          style={{
                            left: '50%',
                            top: '50%',
                            backgroundColor: CONFETTI_COLORS[i % 6],
                          }}
                        />
                      );
                    })}
                  </div>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 12 }}
                    className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center"
                  >
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </motion.div>
                  <h3 className="text-lg font-bold text-white mb-2">
                    {modalIntent === 'collect' ? 'Collect request received' : `You're now following ${artistName}!`}
                  </h3>
                  {modalIntent === 'collect' ? (
                    <p className="text-sm text-text-secondary mb-4">
                      We will mint this pressing for your account as soon as your fan session is active.
                    </p>
                  ) : isNewUser ? (
                    <p className="text-sm text-text-secondary mb-4">
                      Check your email for a welcome message — it contains a link to your personal My Collection page where you can access your collections.
                    </p>
                  ) : (
                    <p className="text-sm text-text-secondary mb-4">
                      You now have access to all available pressings.
                    </p>
                  )}
                  <button
                    onClick={handleCloseModal}
                    className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-medium text-sm transition-colors cursor-pointer"
                  >
                    Done
                  </button>
                </div>
              )}

              {/* Email / Loading / Error States */}
              {modalState !== 'success' && (
                <div className="p-8">
                  {/* Title */}
                  <h3 className="text-center text-white text-lg font-bold mb-2">
                    {modalIntent === 'collect' ? `Collect from ${artistName}` : `Follow ${artistName}`}
                  </h3>
                  <p className="text-center text-white/60 text-sm mb-6">
                    {modalIntent === 'collect'
                      ? 'Enter your email to collect this pressing. We will complete minting when your fan session is active.'
                      : 'Enter your email to follow this artist and get access to exclusive content.'}
                  </p>

                  {/* Email Input */}
                  <div className="space-y-4">
                    <div className="relative">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setModalError(''); }}
                        placeholder="your@email.com"
                        disabled={modalState === 'loading'}
                        className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3.5 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan focus:ring-2 focus:ring-electric-cyan/40 focus:shadow-[0_0_15px_rgba(0,245,255,0.25)] transition-all duration-300 disabled:opacity-50"
                        onKeyDown={(e) => { if (e.key === 'Enter' && modalState === 'email') handleEmailSubmit(); }}
                        autoFocus
                      />
                      {/* Cyan glow on active */}
                      <div className="absolute inset-0 rounded-xl pointer-events-none transition-all duration-300" style={{ boxShadow: email ? '0 0 20px rgba(0,245,255,0.15) inset, 0 0 8px rgba(0,245,255,0.1)' : 'none' }} />
                    </div>

                    {/* Error message */}
                    {modalError && (
                      <motion.p
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-xs text-red-400 px-1"
                      >
                        {modalError}
                      </motion.p>
                    )}

                    {/* Submit Button */}
                    <button
                      onClick={handleEmailSubmit}
                      disabled={modalState === 'loading'}
                      className="w-full py-3.5 rounded-xl font-bold text-sm text-[#0D0D0D] transition-all disabled:opacity-70 cursor-pointer"
                      style={{
                        background: modalState === 'loading' ? 'rgba(0, 245, 255, 0.6)' : 'rgba(0, 245, 255, 0.9)',
                        boxShadow: '0 0 20px rgba(0, 245, 255, 0.3)',
                      }}
                    >
                      {modalState === 'loading' ? (
                        <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                      ) : (
                        modalIntent === 'collect' ? 'Collect' : 'Follow'
                      )}
                    </button>

                    {/* Already a Fan link */}
                    <p className="text-center text-xs text-text-muted mt-2">
                      Already a Fan of another Artist?{' '}
                      <a
                        href={`/fan-login?redirect=${encodeURIComponent(
                          modalIntent === 'collect' && pendingCollectCaptureId
                            ? `/artist/${artistSlug}?collect=${pendingCollectCaptureId}`
                            : `/artist/${artistSlug}?follow=1`
                        )}`}
                        className="text-electric-cyan hover:underline"
                      >
                        Login here.
                      </a>
                    </p>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={handleCloseModal}
                    className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    <X className="w-4 h-4 text-text-muted" />
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Unfollow Confirmation Modal ────────────────────────────────── */}
      <AnimatePresence>
        {showUnfollowConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !unfollowLoading && setShowUnfollowConfirm(false)} />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 60 }}
              transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
              className="relative w-full max-w-md bg-[#1A1A1A]/95 border border-white/10 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl"
            >
              <div className="p-8">
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-red-500/15 flex items-center justify-center">
                  <UserMinus className="w-7 h-7 text-red-400" />
                </div>
                <h3 className="text-center text-white text-lg font-bold mb-3">
                  Unfollow {artistName}?
                </h3>
                <p className="text-center text-white/60 text-sm mb-6 leading-relaxed">
                  You will lose access to any <span className="text-white font-medium">new free pressings</span> this artist releases. Your existing pressings will remain in your catalog.
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowUnfollowConfirm(false)}
                    disabled={unfollowLoading}
                    className="flex-1 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-medium text-sm transition-colors cursor-pointer disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUnfollow}
                    disabled={unfollowLoading}
                    className="flex-1 py-3 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-400 font-bold text-sm transition-colors cursor-pointer border border-red-500/20 disabled:opacity-50"
                  >
                    {unfollowLoading ? (
                      <Loader2 className="w-4 h-4 mx-auto animate-spin" />
                    ) : (
                      'Unfollow'
                    )}
                  </button>
                </div>

                {/* Close button */}
                {!unfollowLoading && (
                  <button
                    onClick={() => setShowUnfollowConfirm(false)}
                    className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    <X className="w-4 h-4 text-text-muted" />
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Report Modal ────────────────────────────────────────────────── */}
      {showReport && tenantData && (
        <ReportArtistModal
          tenantId={tenantData.id}
          artistName={tenantData.artistName}
          onClose={() => setShowReport(false)}
        />
      )}

      <CaptureShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        captureId={shareCapture?.id ?? null}
        captureTitle={shareCapture?.title || null}
        artistName={artistName}
        suggestions={shareCapture?.sharingSuggestions || []}
      />
    </div>
  );
}
